import { Launch } from '@lightningjs/sdk'

import isWebos from '@accedo/xdk-device-lg-webos/esm/detection'
import App from './App'
import { createKeyMapping } from './config/createKeyMapping'
import './preflight'
import { isLowEndDevice } from './utils/info'
import { getViewportSize } from './utils/viewport'

import FloLogo from '../static/images/logo-large.svg'

export default function (appSettings, platformSettings, appData) {
  const renderCanvasAt720p = () => {
    appSettings.stage.precision = 0.66667
    appSettings.stage.w = 1280
    appSettings.stage.h = 720

    const { width, height } = getViewportSize()
    const isNative720p = isWebos && width === 1280 && height === 720

    const canvasSize = isNative720p ? { w: width, h: height } : { w: 1920, h: 1080 }

    document.styleSheets[0].insertRule(
      `canvas { width: ${canvasSize.w}px; height: ${canvasSize.h}px; }`,
      0,
    )
  }

  // This prevents the user from seeing a white screen when launching the app
  // and until the splash screen starts playing
  document.body.style.backgroundColor = '#0C0C0C'

  const logo = document.createElement('img')
  logo.id = 'flo-logo'
  logo.src = FloLogo
  logo.alt = 'FloSports Logo'
  logo.style.position = 'absolute'
  logo.style.top = '50%'
  logo.style.left = '50%'
  logo.style.transform = 'translate(-50%, -50%)'
  logo.style.width = '29vw'
  logo.style.height = '7vh'

  // Append the image to the body
  document.body.appendChild(logo)

  // The reason we are doing this here, instead of populating settings.json
  // is because the developer of Lightning completely unaware that number
  // is not a valid key in JSON file. Any linters or code formatter will
  // throw error because of that.

  const keyMapping = createKeyMapping()

  // If not key mapping returned we skip the set up and
  // use Lightning's default key map
  if (keyMapping) {
    appSettings.keys = createKeyMapping()
  }

  appSettings.env = {
    APP_ENABLE_TTS: process.env.APP_ENABLE_TTS,
    APP_ENABLE_POINTER: process.env.APP_ENABLE_POINTER,
    // Tizen Subscription keys
    APP_TIZEN_ID: process.env.APP_TIZEN_ID,
    APP_TIZEN_SECURITY_KEY: process.env.APP_TIZEN_SECURITY_KEY,
    // Vizio Subscription keys
    APP_VIZIO_APP_KEY_ID_STAGING: process.env.APP_VIZIO_APP_KEY_ID_STAGING,
    APP_VIZIO_APP_KEY_ID_PROD: process.env.APP_VIZIO_APP_KEY_ID_PROD,
    // Firebase keys
    APP_FIREBASE_API_KEY: process.env.APP_FIREBASE_API_KEY,
    APP_FIREBASE_APP_ID: process.env.APP_FIREBASE_APP_ID,
    APP_FIREBASE_DB_URL: process.env.APP_FIREBASE_DB_URL,
    APP_FIREBASE_PROJECT_ID: process.env.APP_FIREBASE_PROJECT_ID,

    APP_FIREBASE_STAGING_API_KEY: process.env.APP_FIREBASE_STAGING_API_KEY,
    APP_FIREBASE_STAGING_APP_ID: process.env.APP_FIREBASE_STAGING_APP_ID,
    APP_FIREBASE_STAGING_DB_URL: process.env.APP_FIREBASE_STAGING_DB_URL,
    APP_FIREBASE_STAGING_PROJECT_ID: process.env.APP_FIREBASE_STAGING_PROJECT_ID,

    APP_FIREBASE_SPORT_DATA_API_KEY: process.env.APP_FIREBASE_SPORT_DATA_API_KEY,
    APP_FIREBASE_SPORT_DATA_APP_ID: process.env.APP_FIREBASE_SPORT_DATA_APP_ID,
    APP_FIREBASE_SPORT_DATA_DB_URL: process.env.APP_FIREBASE_SPORT_DATA_DB_URL,
    APP_FIREBASE_SPORT_DATA_PROJECT_ID: process.env.APP_FIREBASE_SPORT_DATA_PROJECT_ID,

    APP_STAGING_FIREBASE_SPORT_DATA_API_KEY: process.env.APP_STAGING_FIREBASE_SPORT_DATA_API_KEY,
    APP_STAGING_FIREBASE_SPORT_DATA_APP_ID: process.env.APP_STAGING_FIREBASE_SPORT_DATA_APP_ID,
    APP_STAGING_FIREBASE_SPORT_DATA_DB_URL: process.env.APP_STAGING_FIREBASE_SPORT_DATA_DB_URL,
    APP_STAGING_FIREBASE_SPORT_DATA_PROJECT_ID:
      process.env.APP_STAGING_FIREBASE_SPORT_DATA_PROJECT_ID,
    // Segment keys
    APP_SEGMENT_WRITE_KEY: process.env.APP_SEGMENT_WRITE_KEY,
    // Datadog keys
    APP_DATADOG_CLIENT_TOKEN: process.env.APP_DATADOG_CLIENT_TOKEN,
    APP_DATADOG_SAMSUNG_RUM_CLIENT_TOKEN: process.env.APP_DATADOG_SAMSUNG_RUM_CLIENT_TOKEN,
    APP_DATADOG_SAMSUNG_RUM_APP_ID: process.env.APP_DATADOG_SAMSUNG_RUM_APP_ID,
    APP_DATADOG_VIZIO_RUM_CLIENT_TOKEN: process.env.APP_DATADOG_VIZIO_RUM_CLIENT_TOKEN,
    APP_DATADOG_VIZIO_RUM_APP_ID: process.env.APP_DATADOG_VIZIO_RUM_APP_ID,
    APP_DATADOG_LG_RUM_CLIENT_TOKEN: process.env.APP_DATADOG_LG_RUM_CLIENT_TOKEN,
    APP_DATADOG_LG_RUM_APP_ID: process.env.APP_DATADOG_LG_RUM_APP_ID,
    // Youbora keys
    APP_YOUBORA_ACCOUNT_CODE_PROD: process.env.APP_YOUBORA_ACCOUNT_CODE_PROD,
    APP_YOUBORA_ACCOUNT_CODE_STAGING: process.env.APP_YOUBORA_ACCOUNT_CODE_STAGING,
    APP_YOUBORA_HOST_PROD: process.env.APP_YOUBORA_HOST_PROD,
    APP_YOUBORA_HOST_STAGING: process.env.APP_YOUBORA_HOST_STAGING,
    // App keys
    APP_VERSION: process.env.APP_VERSION,
  }

  if (window.tizen) {
    try {
      const tizenVersion = Number(
        tizen.systeminfo.getCapability('http://tizen.org/feature/platform.version'),
      )

      if (tizenVersion <= 6.0) {
        if (tizenVersion <= 5.5) {
          appSettings.stage.readPixelsAfterDraw = true
          appSettings.stage.useImageWorker = false // MUST be disabled if readPixelsAfterDraw is enabled, otherwise images do not load
        }

        renderCanvasAt720p()
      }
    } catch (e) {
      console.error(
        'There was a problem adjusting the canvas for Tizen low end devices. Reason:',
        e,
      )
    }
  }

  if (isWebos) {
    try {
      if (isLowEndDevice()) {
        // The next two settings might not be needed, needs testing
        appSettings.stage.readPixelsAfterDraw = true
        appSettings.stage.useImageWorker = false

        renderCanvasAt720p()
      }
    } catch (e) {
      console.error(
        'There was a problem adjusting the canvas for WebOs low end devices. Reason:',
        e,
      )
    }
  }

  console.debug('appSettings ', appSettings)
  console.debug('platformSettings ', platformSettings)
  console.debug('appData ', appData)

  return Launch(App, appSettings, platformSettings, appData)
}
